/**
 * Member profile fields registry API wrapper (v2 — Project Independence, DNKADM-0058).
 *
 * The **member-profile-fields registry** (handoff §5.13) is a NEW v2 entity (no v1
 * equivalent; it SUPERSEDED the v1 custom-fields/classifications model `customFields.ts`,
 * now retired — DNKADM-0074): the per-tenant catalog of which member-profile fields exist
 * and **who owns each** + whether a member may write it. It is the registry the member
 * profile editor (0059) reads to decide pass-through vs read-only.
 *
 * **Ownership semantics (§4.0).** `fieldOwnership` is `GHL` (the field is owned by the
 * external contact system — read-through, that system wins) or `DINK` (we
 * compute/store, we win). `isMemberWritable` is **orthogonal** to ownership: a
 * `GHL`+writable field passes member edits through to the contact system, a
 * `GHL`+not-writable field is read-only. At launch there are no DINK-owned free-form
 * rows (credits/membership are computed elsewhere), so this table may **start empty** —
 * the CRUD exists for per-tenant extension. **A NEW field defaults to `GHL` +
 * not-writable (the safe choice).**
 *
 * **White-label (§4.0 / CLAUDE.md).** `fieldOwnership` (`GHL|DINK`) and
 * `ghlCustomFieldKey` are **DATA** carried/submitted as-is here — they must NEVER reach
 * the UI as the raw brand. Ownership renders via `fieldOwnershipLabel`
 * (`GHL` → "External / contact system", `DINK` → "Managed here") and the contact-system
 * key via `CONTACT_SYSTEM_FIELD_KEY_LABEL` (`src/utils/whitelabel.ts`).
 *
 * Bearer-only `/v2/admin/**` surface — `request()` routes it (DNKADM-0049
 * `isV2Surface`): bearer token, NO CSRF, NO cookies, the bounded 401→refresh→retry; the
 * mutating-audit `X-Session-Mode` stamp fires on the writes (`isV2AdminSurface`).
 *
 * **Endpoints (handoff §5.13, §3.1 — standard REST + soft-delete; ASSUMED standard
 * shapes, recorded in the DNKADM-0058 delivery's Notes):**
 *  - `GET    /v2/admin/member-profile-fields?status=active|inactive|all` → {@link MemberProfileField}`[]`.
 *  - `POST   /v2/admin/member-profile-fields` ← {@link MemberProfileFieldInput} → the created row.
 *  - `PUT    /v2/admin/member-profile-fields/{id}` ← {@link MemberProfileFieldInput} → the updated row.
 *  - `POST   /v2/admin/member-profile-fields/{id}/deactivate` | `/reactivate` (§4.3).
 *
 * **`v2_member_profile_field` field set (handoff §5.13):** `fieldKey`, `fieldLabel`,
 * `fieldType` (`text|number|date|single_options|multi_options` — option
 * types per DNKAPI-0439/DNKADM-0275; `boolean` removed by DNKADM-0281; the
 * legacy `select` spelling reads as `single_options`, DNKADM-0282),
 * `fieldOwnership` (`GHL|DINK`),
 * `isMemberWritable` (boolean, orthogonal to ownership), `ghlCustomFieldKey` (the
 * contact-system target/source key), `isActive`. Unique on `(tenant, fieldKey)` — the
 * create/update `409` surfaces inline.
 *
 * Every read is parsed DEFENSIVELY (mirrors `services.ts` / `creditBuckets.ts`): a
 * missing/wrong-typed field degrades (an entry with no usable `id` is dropped), never a
 * throw.
 */
import { request } from './client';
import { useCallback } from 'react';
import { usePickList, type PickList } from '@/hooks/usePickList';
import { PickListError } from './picklists';
import type { ActiveFilterValue } from '@/hooks/useActiveFilter';
import {
  FIELD_OWNERSHIPS,
  normalizeFieldOwnership,
  type FieldOwnership,
} from '@/utils/whitelabel';

// Re-export the ownership type + values (DEFINED in the allow-listed scrub-source
// module so the raw `'GHL'` literal never lives in ordinary source) for the registry
// screen + the 0059 consumer to import from this entity module alongside the rest.
export { FIELD_OWNERSHIPS, type FieldOwnership };

/** v2 admin member-profile-fields base path (bearer surface). */
const PATH = '/v2/admin/member-profile-fields';

/**
 * Whether the backend registry carries an option field's **`options`** list
 * (DNKADM-0270 §Backend Dependency; shipped by DNKAPI-0439, flipped by
 * DNKADM-0275). The DNKAPI-0439 contract this module is wired to:
 *  - `options: string[]`, **omitted from JSON when absent** — never `null`,
 *    never `[]` (`@JsonInclude(NON_NULL)` on the response; an omitted request
 *    key means "not supplied" and leaves stored options untouched on update).
 *  - `options` is accepted ONLY on a **DINK-owned** option-type field
 *    (`single_options`/`multi_options`). A non-null `options` on any other
 *    type OR on a **GHL-owned** field is rejected `400` — **even an empty
 *    array** (the ownership check fires before emptiness). A GHL-owned
 *    option field's choices come from the contact system's picklist.
 *  - A supplied list must have ≥1 entry, trimmed; blanks and duplicates
 *    (post-trim exact match) are rejected `400`.
 */
export const MEMBER_PROFILE_FIELD_OPTIONS_SUPPORTED = true;

/**
 * A field's data type (handoff §5.13; option types corrected by DNKADM-0275).
 * `single_options`/`multi_options` are the backend's option-bearing wire
 * values (dink-api `normalizeDinkFieldType`, DNKAPI-0439). The legacy FE
 * `select` spelling — which every pre-0275 option row is stored under
 * (DNKAPI-0440 registry dump) — is a READ-COMPAT ALIAS for `single_options`
 * ({@link parseField}, DNKADM-0282): the row loads as single-choice and its
 * next save writes the canonical `single_options`, never `text` (which would
 * make the backend discard its stored options, DNKAPI-0439). `select` is
 * never offered in the dialog menu and never written.
 */
export type FieldType =
  | 'text'
  | 'number'
  | 'date'
  | 'single_options'
  | 'multi_options';

/**
 * All field types, in form-display order. `boolean` ("Yes / no") was removed by
 * DNKADM-0281: dink-api has no boolean type (`normalizeDinkFieldType` sends it to
 * TEXT) and the member app has no checkbox renderer, so it was a misleading no-op
 * — a yes/no preference is a DINK-owned `single_options` field (e.g.
 * `["Yes","No"]`). A stored `boolean` row degrades to `text` in
 * {@link parseField}, matching the server's own normalization.
 */
export const FIELD_TYPES: readonly FieldType[] = [
  'text',
  'number',
  'date',
  'single_options',
  'multi_options',
] as const;

const FIELD_TYPE_LABELS: Record<FieldType, string> = {
  text: 'Text',
  number: 'Number',
  date: 'Date',
  single_options: 'Single choice',
  multi_options: 'Multiple choice',
};

/**
 * Whether a type is option-bearing (`single_options`/`multi_options`) —
 * mirrors dink-api's `ProfileFieldValue.isOptionType`. Only a DINK-owned
 * field of one of these types may carry `options` (DNKAPI-0439).
 */
export function isOptionFieldType(type: FieldType): boolean {
  return type === 'single_options' || type === 'multi_options';
}

/** Human label for a field type (form/option/list display). */
export function fieldTypeLabel(type: FieldType): string {
  return FIELD_TYPE_LABELS[type];
}

/**
 * One `v2_member_profile_field` row (§5.13). `ghlCustomFieldKey` is an optional string
 * (absent when the server carries none — `exactOptionalPropertyTypes`: the key is
 * omitted, never `undefined`). `fieldOwnership` + `ghlCustomFieldKey` are DATA rendered
 * brand-neutral (§4.0). `isMemberWritable` is orthogonal to ownership.
 */
export interface MemberProfileField {
  id: string;
  /** The stable field key — unique on `(tenant, fieldKey)`. */
  fieldKey: string;
  /** The human display label shown in the member profile editor (0059). */
  fieldLabel: string;
  /** The field's data type (text|number|date|single_options|multi_options). */
  fieldType: FieldType;
  /** Who owns the field (`GHL`/`DINK`) — DATA, rendered brand-neutral. */
  fieldOwnership: FieldOwnership;
  /** Whether a member may write the field — ORTHOGONAL to ownership. */
  isMemberWritable: boolean;
  /** Soft-delete flag (§4.3); `false` = deactivated. */
  isActive: boolean;
  /**
   * The contact-system target/source key (§5.13) — DATA, decoupled from the label.
   * Rendered brand-neutral (§4.0): never as "GHL …".
   */
  ghlCustomFieldKey?: string;
  /**
   * An option field's choices (DNKADM-0270/DNKAPI-0439) — omitted from the
   * wire when the field has none (never `null`/`[]`); assigned only when the
   * wire carries a string array (blank entries dropped); absent otherwise.
   * A GHL-owned option field legitimately has none stored here (its choices
   * are the contact system's picklist, composed downstream on `/v2/me`).
   */
  options?: string[];
}

/**
 * Create/update body for a member profile field. `fieldKey` + `fieldLabel` are required
 * by the form; `fieldType`/`fieldOwnership`/`isMemberWritable` are required (defaulted in
 * the form to the SAFE `GHL` + not-writable). `ghlCustomFieldKey` is optional.
 * `isActive` is NOT settable here — activation is the dedicated
 * deactivate/reactivate endpoints (§4.3).
 */
export interface MemberProfileFieldInput {
  fieldKey: string;
  fieldLabel: string;
  fieldType: FieldType;
  fieldOwnership: FieldOwnership;
  isMemberWritable: boolean;
  ghlCustomFieldKey?: string;
  /**
   * A DINK-owned option field's choices (DNKADM-0270/DNKAPI-0439) — set by
   * the dialog ONLY for a `single_options`/`multi_options` field with DINK
   * ownership, when the choices are being (re)defined: ≥1 entry, trimmed,
   * deduped. MUST stay absent otherwise — the backend 400s on a non-null
   * `options` (even `[]`) for any other type or for GHL ownership, and an
   * omitted key is what leaves stored options untouched on update.
   */
  options?: string[];
}

/** Options for {@link listMemberProfileFields}. */
export interface ListMemberProfileFieldsOptions {
  /** Active/Inactive/All filter (§4.3); defaults to `active` (live rows only). */
  status?: ActiveFilterValue;
  /**
   * Convenience flag (mirrors the spec's `listMemberProfileFields({ includeInactive? })`):
   * `true` ⇒ `status=all`. Ignored when an explicit `status` is also passed.
   */
  includeInactive?: boolean;
}

// --- Defensive parsing helpers (mirrors services.ts / creditBuckets.ts) ---------

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

/** A trimmed non-empty string, or null. */
function asText(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed.length === 0 ? null : trimmed;
}

/**
 * A stable string id from a NUMBER or a string (DNKADM-0064): v2 entity ids arrive
 * as numbers on the wire and are normalized to the `string` the UI/router use — a
 * finite number → `String(n)`, a non-empty string → trimmed, else null. Preserves
 * genuinely-string ids (accepts both); a numeric id is never dropped.
 */
function asId(value: unknown): string | null {
  if (typeof value === 'number') return Number.isFinite(value) ? String(value) : null;
  return asText(value);
}

/** A boolean from a real boolean, or a fallback when absent/unparseable. */
function asBoolean(value: unknown, fallback: boolean): boolean {
  return typeof value === 'boolean' ? value : fallback;
}

/**
 * Parse a raw field type. The legacy `select` spelling reads as
 * `single_options` (DNKADM-0282 — every pre-0275 option row is stored with it,
 * so degrading it to `text` would lose the row's option-ness and, on the next
 * save, its stored options). Anything else unknown — including the removed
 * `boolean` (DNKADM-0281), which the server itself normalizes to TEXT
 * (dink-api `normalizeDinkFieldType`) — degrades to the safe `text` default.
 */
function asFieldType(value: unknown): FieldType {
  if (typeof value === 'string') {
    const lower = value.toLowerCase();
    if (lower === 'select') return 'single_options';
    if ((FIELD_TYPES as readonly string[]).includes(lower)) return lower as FieldType;
  }
  return 'text';
}

/**
 * Parse one raw field row, or null when it has no usable `id` (a half-identified entry
 * is dropped rather than surfaced as a selectable option). A missing `fieldKey`/
 * `fieldLabel` degrades to an empty string (the row still renders; the server is the
 * authority). `fieldType` degrades to `text`, `fieldOwnership` to the safe `GHL`,
 * `isMemberWritable` to `false` (safe). `ghlCustomFieldKey` is assigned only when
 * present (exactOptionalPropertyTypes).
 */
function parseField(raw: unknown): MemberProfileField | null {
  if (!isObject(raw)) return null;
  const id = asId(raw['id']);
  if (id === null) return null;

  const field: MemberProfileField = {
    id,
    fieldKey: asText(raw['fieldKey']) ?? '',
    fieldLabel: asText(raw['fieldLabel']) ?? '',
    fieldType: asFieldType(raw['fieldType']),
    fieldOwnership: normalizeFieldOwnership(raw['fieldOwnership']),
    isMemberWritable: asBoolean(raw['isMemberWritable'], false),
    // The wire flag is `active` (DNKADM-0064); tolerate legacy `isActive`.
    isActive: asBoolean(raw['active'] ?? raw['isActive'], true),
  };
  // `ghlCustomFieldKey` is a genuine external target/source key string — keep asText.
  const ghlCustomFieldKey = asText(raw['ghlCustomFieldKey']);
  if (ghlCustomFieldKey !== null) field.ghlCustomFieldKey = ghlCustomFieldKey;
  // Forward-tolerant `options` read (DNKADM-0270): a string array parses (blank
  // entries dropped); anything else stays absent — never a crash on the old wire.
  if (Array.isArray(raw['options'])) {
    const options = raw['options']
      .map((o) => (typeof o === 'string' ? o.trim() : ''))
      .filter((o) => o !== '');
    if (options.length > 0) field.options = options;
  }
  return field;
}

/**
 * Extract the fields array TOLERANTLY from a successful body, returning `null` when
 * NEITHER a bare array NOR a known wrapper key
 * (`memberProfileFields`/`fields`/`content`) yields one. The list read turns `null`
 * into `[]` (an empty list is a real first-run state, §8); the fail-closed pick-list
 * turns it into a {@link PickListError} (a non-array success cannot be trusted as
 * "complete and empty").
 */
function extractFields(body: unknown): unknown[] | null {
  if (Array.isArray(body)) return body;
  if (isObject(body)) {
    if (Array.isArray(body['memberProfileFields'])) return body['memberProfileFields'];
    if (Array.isArray(body['fields'])) return body['fields'];
    if (Array.isArray(body['content'])) return body['content'];
  }
  return null;
}

/** Build the JSON body for a create/update, omitting an absent `ghlCustomFieldKey`. */
function toBody(input: MemberProfileFieldInput): Record<string, unknown> {
  const json: Record<string, unknown> = {
    fieldKey: input.fieldKey,
    fieldLabel: input.fieldLabel,
    fieldType: input.fieldType,
    fieldOwnership: input.fieldOwnership,
    isMemberWritable: input.isMemberWritable,
  };
  if (input.ghlCustomFieldKey !== undefined) {
    json['ghlCustomFieldKey'] = input.ghlCustomFieldKey;
  }
  // Sent only when the dialog supplied choices for a DINK-owned option field
  // (DNKADM-0275) — never an empty/placeholder array, and never any `options`
  // key for a GHL-owned or non-option field (the backend 400s on one,
  // DNKAPI-0439); an omitted key leaves stored options untouched on update.
  if (input.options !== undefined) json['options'] = input.options;
  return json;
}

function fieldPath(id: string): string {
  return `${PATH}/${encodeURIComponent(id)}`;
}

// --- Endpoints -----------------------------------------------------------------

/**
 * `GET /v2/admin/member-profile-fields?status=…` → the tenant's member profile fields,
 * defensively parsed (a bad entry dropped). The `status` filter (§4.3) defaults to
 * `active` (live rows only); pass `status: 'inactive'|'all'` or `includeInactive: true`
 * to include deactivated rows. A body that yields no array degrades to `[]` (a real
 * first-run state, §8 — the table may legitimately start empty). Throws a typed
 * `ApiError` on a non-2xx response.
 */
export async function listMemberProfileFields(
  options: ListMemberProfileFieldsOptions = {},
): Promise<MemberProfileField[]> {
  const status = options.status ?? (options.includeInactive ? 'all' : 'active');
  const body = await request<unknown>('GET', PATH, { query: { status } });
  return (extractFields(body) ?? [])
    .map(parseField)
    .filter((f): f is MemberProfileField => f !== null);
}

/**
 * `POST /v2/admin/member-profile-fields` → create a field. Returns the re-parsed created
 * row. Throws a typed `ApiError` (`400` VALIDATION inline-able, `409` CONFLICT on the
 * unique `(tenant, fieldKey)` rule, upstream).
 */
export async function createMemberProfileField(
  input: MemberProfileFieldInput,
): Promise<MemberProfileField | null> {
  const body = await request<unknown>('POST', PATH, { json: toBody(input) });
  return parseField(body);
}

/**
 * `PUT /v2/admin/member-profile-fields/{id}` → update a field. Returns the re-parsed
 * updated row. Throws a typed `ApiError`.
 */
export async function updateMemberProfileField(
  id: string,
  input: MemberProfileFieldInput,
): Promise<MemberProfileField | null> {
  const body = await request<unknown>('PUT', fieldPath(id), { json: toBody(input) });
  return parseField(body);
}

/**
 * `POST /v2/admin/member-profile-fields/{id}/deactivate` → soft-deactivate a field
 * (§4.3 — never a hard delete). Throws a typed `ApiError`.
 */
export async function deactivateMemberProfileField(id: string): Promise<void> {
  await request<unknown>('POST', `${fieldPath(id)}/deactivate`);
}

/**
 * `POST /v2/admin/member-profile-fields/{id}/reactivate` → reactivate a
 * soft-deactivated field (§4.3). Throws a typed `ApiError`.
 */
export async function reactivateMemberProfileField(id: string): Promise<void> {
  await request<unknown>('POST', `${fieldPath(id)}/reactivate`);
}

// --- The shared, fail-closed member-profile-field list (drives 0059) ------------

/**
 * Load the ACTIVE member profile fields, **fail-closed**: any upstream/network failure
 * OR a malformed (non-array) `200` body throws the shared {@link PickListError}, so the
 * consumer (the 0059 member profile editor) renders error + Retry and NEVER a silent
 * empty list (a failure can never masquerade as "loaded, empty" — which would let the
 * editor wrongly conclude a field is unregistered / safe to edit). A successful empty
 * list returns `[]` (a real first-run state). Only `active` fields are offered.
 *
 * Mirrors the `@/api/creditBuckets` / `@/api/resources` fail-closed wrappers, reusing
 * their `PickListError` so `usePickList`'s `failClosed` detection (and the loader copy)
 * work unchanged.
 */
export async function getMemberProfileFields(): Promise<MemberProfileField[]> {
  try {
    const body = await request<unknown>('GET', PATH, { query: { status: 'active' } });
    const list = extractFields(body);
    // A non-array success cannot be trusted as a COMPLETE list — fail-closed.
    if (list === null) throw new Error('expected an array of member profile fields');
    return list.map(parseField).filter((f): f is MemberProfileField => f !== null);
  } catch (cause) {
    throw new PickListError('member-profile-fields', cause);
  }
}

/**
 * `useMemberProfileFieldList()` — the fail-closed member-profile-field list the member
 * profile editor (0059) imports to decide, per field, pass-through vs read-only.
 *
 * Loads live via {@link getMemberProfileFields} through `usePickList` (fail-closed):
 * `loading → ready` with the `MemberProfileField[]`, or `loading → error + Retry` —
 * never a partial/empty-on-failure list. Render through `<PickListLoader>`
 * (`@/components/PickListLoader`).
 */
export function useMemberProfileFieldList(): PickList<MemberProfileField> {
  return usePickList(useCallback(() => getMemberProfileFields(), []), []);
}
